import {
	ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import { getSurfaceBorderStyle } from "@/app/application/theme";
import { useTime } from "@/app/application/TimeContext";
import { useDebouncedValue } from "@/shared/hooks";
import SearchBar from "@/shared/ui/search-bar";
import SortMenu from "@/shared/ui/sort-menu/SortMenu";
import {
	Box,
	CircularProgress,
	Pagination,
	PaginationItem,
	Stack,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";

const SEARCH_DEBOUNCE_MS = 300;

type Props<T> = {
	items: T[];
	itemsPerPage: number;
	children: (items: T[]) => ReactNode;
	getItemName: (item: T) => string;
	getItemDate: (item: T) => Date | string | undefined;
	initialSortOrder?: "ASC" | "DESC";
	isLoading?: boolean;
	emptyState?: ReactNode;
	/** Placeholder for the search field */
	searchPlaceholder?: string;
	/** When server-side: debounce search before calling onSearchChange (ms). Default 300. */
	searchDebounceMs?: number;
	/** When true, uses flex layout so list scrolls and pagination stays visible in viewport */
	fillHeight?: boolean;
	/** When true, hides the search field */
	hideSearch?: boolean;
	serverSidePagination?: {
		currentPage: number;
		totalPages: number;
		totalCount?: number;
		onPageChange: (page: number) => void;
		onSearchChange: (search: string) => void;
		onSortChange: (sort: "ASC" | "DESC") => void;
	};
};

const PaginatedSearchWrapper = <T,>({
	items,
	itemsPerPage = 10,
	initialSortOrder = "ASC",
	children,
	getItemName,
	getItemDate,
	isLoading = false,
	emptyState = null,
	searchPlaceholder = "Search…",
	searchDebounceMs = SEARCH_DEBOUNCE_MS,
	fillHeight = false,
	hideSearch = false,
	serverSidePagination,
}: Props<T>) => {
	const theme = useTheme();
	const { timezone } = useTime();

	const [searchParams, setSearchParams] = useState<string>("");
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">(initialSortOrder);

	const isServerSide = !!serverSidePagination;
	const debouncedSearch = useDebouncedValue(
		searchParams,
		isServerSide ? searchDebounceMs : 0,
	);

	const onSearchChangeRef = useRef(serverSidePagination?.onSearchChange);
	onSearchChangeRef.current = serverSidePagination?.onSearchChange;

	useEffect(() => {
		if (isServerSide && onSearchChangeRef.current) {
			onSearchChangeRef.current(debouncedSearch);
		}
	}, [debouncedSearch, isServerSide]);

	// Client-side: reset to page 1 when search or sort changes
	useEffect(() => {
		if (!isServerSide) setCurrentPage(1);
	}, [searchParams, sortOrder, isServerSide]);

	const filteredSorted = useMemo(() => {
		const filtered = isServerSide
			? items
			: (() => {
					const needle = searchParams.trim().toLowerCase();
					return items.filter((item) =>
						getItemName(item)?.toLowerCase().includes(needle),
					);
				})();

		if (isServerSide) return filtered;

		const sorted = [...filtered].sort((a, b) => {
			const dateA = dayjs(getItemDate(a)).tz(timezone);
			const dateB = dayjs(getItemDate(b)).tz(timezone);
			if (sortOrder === "ASC") return dateA.isAfter(dateB) ? 1 : -1;
			return dateA.isBefore(dateB) ? 1 : -1;
		});
		return sorted;
	}, [
		items,
		searchParams,
		sortOrder,
		timezone,
		getItemName,
		getItemDate,
		isServerSide,
	]);

	const effectivePage = isServerSide
		? serverSidePagination.currentPage
		: currentPage;
	const effectiveTotalPages = isServerSide
		? serverSidePagination.totalPages
		: Math.max(1, Math.ceil(filteredSorted.length / itemsPerPage));
	const current = Math.min(effectivePage, effectiveTotalPages);
	const totalCount = isServerSide
		? (serverSidePagination.totalCount ?? filteredSorted.length)
		: filteredSorted.length;
	const showResultCount =
		!isServerSide || serverSidePagination.totalCount !== undefined;

	const paginatedItems = useMemo(() => {
		if (isServerSide) return filteredSorted;
		return filteredSorted.slice(
			(current - 1) * itemsPerPage,
			current * itemsPerPage,
		);
	}, [filteredSorted, current, itemsPerPage, isServerSide]);

	const handlePageChange = useCallback(
		(_: React.ChangeEvent<unknown>, page: number) => {
			if (isServerSide) {
				serverSidePagination.onPageChange(page);
			} else {
				setCurrentPage(page);
			}
		},
		[isServerSide, serverSidePagination],
	);

	const handleSortChange = useCallback(
		(newSort: "ASC" | "DESC") => {
			setSortOrder(newSort);
			if (isServerSide && serverSidePagination?.onSortChange) {
				serverSidePagination.onSortChange(newSort);
			}
		},
		[isServerSide, serverSidePagination],
	);

	const toolbarSx = useMemo(
		() => ({
			px: 1.5,
			py: 1.25,
			mb: 1.5,
			...(hideSearch
				? {}
				: {
						bgcolor: "background.paper",
						borderRadius: 2,
						border: getSurfaceBorderStyle(theme, "subtle"),
					}),
		}),
		[theme, hideSearch],
	);

	if (isLoading) {
		return (
			<Box>
				<Stack
					direction={{ xs: "column", sm: "row" }}
					spacing={1.5}
					alignItems="center"
					justifyContent="space-between"
					flexWrap="wrap"
					sx={toolbarSx}
				>
					{hideSearch ? (
						<Box sx={{ flex: 1 }} />
					) : (
						<SearchBar
							searchParams={searchParams}
							setSearchParams={setSearchParams}
							disabled
							placeholder={searchPlaceholder}
						/>
					)}
					<SortMenu
						sortOrder={sortOrder}
						setSortOrder={handleSortChange}
						disabled
					/>
				</Stack>
				<Box
					sx={{
						display: "grid",
						placeItems: "center",
						py: 8,
						color: "text.secondary",
					}}
				>
					<CircularProgress color="primary" size={40} />
				</Box>
			</Box>
		);
	}

	const rootSx = fillHeight
		? {
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}
		: {};
	const contentWrapperSx = fillHeight
		? {
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}
		: {};
	const listWrapperSx = fillHeight
		? {
				flex: 1,
				minHeight: 0,
				overflow: "hidden",
				display: "flex",
				flexDirection: "column",
			}
		: {};
	const paginationBoxSx = fillHeight
		? {
				flexShrink: 0,
				display: "flex",
				justifyContent: "flex-end",
				alignItems: "center",
				mt: 2,
				py: 1,
				borderTop: getSurfaceBorderStyle(theme, "subtle"),
			}
		: {
				display: "flex",
				justifyContent: "flex-end",
				alignItems: "center",
				mt: 2,
				py: 1,
				borderTop: getSurfaceBorderStyle(theme, "subtle"),
			};

	return (
		<Box sx={rootSx}>
			<Stack
				direction={{ xs: "column", sm: "row" }}
				spacing={1.5}
				alignItems="center"
				justifyContent="space-between"
				flexWrap="wrap"
				sx={{ ...toolbarSx, ...(fillHeight ? { flexShrink: 0 } : {}) }}
			>
				{hideSearch ? (
					<Box sx={{ flex: 1 }} />
				) : (
					<SearchBar
						searchParams={searchParams}
						setSearchParams={setSearchParams}
						placeholder={searchPlaceholder}
					/>
				)}
				<Stack direction="row" spacing={1} alignItems="center">
					{showResultCount && (
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ fontWeight: 600 }}
						>
							{filteredSorted.length === 0
								? "No results"
								: `${totalCount} ${totalCount === 1 ? "item" : "items"}`}
						</Typography>
					)}
					<SortMenu sortOrder={sortOrder} setSortOrder={handleSortChange} />
				</Stack>
			</Stack>

			{filteredSorted.length === 0 ? (
				(emptyState ?? null)
			) : fillHeight ? (
				<Box sx={contentWrapperSx}>
					<Box sx={listWrapperSx}>{children(paginatedItems)}</Box>
					{effectiveTotalPages > 1 && (
						<Box sx={paginationBoxSx}>
							<Pagination
								color="primary"
								size="small"
								count={effectiveTotalPages}
								page={current}
								onChange={handlePageChange}
								renderItem={(item) => (
									<PaginationItem
										{...item}
										shape="rounded"
										variant="outlined"
										sx={{
											borderRadius: 2,
											minWidth: 34,
											height: 32,
											fontWeight: 600,
											borderColor: "divider",
											bgcolor: "background.paper",
											"&:hover": {
												boxShadow: 2,
												borderColor: "primary.light",
												bgcolor: (t) =>
													alpha(
														t.palette.primary.main,
														t.palette.mode === "dark" ? 0.12 : 0.06,
													),
											},
											"&.Mui-focusVisible": {
												boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}`,
											},
											"&.Mui-selected": {
												bgcolor: "primary.main",
												color: "primary.contrastText",
												borderColor: "primary.main",
												"&:hover": { bgcolor: "primary.dark" },
											},
											"&.MuiPaginationItem-previousNext": { px: 1.25 },
											"&.MuiPaginationItem-ellipsis": { mx: 0.5 },
										}}
									/>
								)}
								sx={{
									"& .MuiPagination-ul": {
										gap: 0.5,
										justifyContent: "flex-end",
									},
								}}
							/>
						</Box>
					)}
				</Box>
			) : (
				<>
					{children(paginatedItems)}
					{effectiveTotalPages > 1 && (
						<Box sx={paginationBoxSx}>
							<Pagination
								color="primary"
								size="small"
								count={effectiveTotalPages}
								page={current}
								onChange={handlePageChange}
								renderItem={(item) => (
									<PaginationItem
										{...item}
										shape="rounded"
										variant="outlined"
										sx={{
											borderRadius: 2,
											minWidth: 34,
											height: 32,
											fontWeight: 600,
											borderColor: "divider",
											bgcolor: "background.paper",
											"&:hover": {
												boxShadow: 2,
												borderColor: "primary.light",
												bgcolor: (t) =>
													alpha(
														t.palette.primary.main,
														t.palette.mode === "dark" ? 0.12 : 0.06,
													),
											},
											"&.Mui-focusVisible": {
												boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}`,
											},
											"&.Mui-selected": {
												bgcolor: "primary.main",
												color: "primary.contrastText",
												borderColor: "primary.main",
												"&:hover": { bgcolor: "primary.dark" },
											},
											"&.MuiPaginationItem-previousNext": { px: 1.25 },
											"&.MuiPaginationItem-ellipsis": { mx: 0.5 },
										}}
									/>
								)}
								sx={{
									"& .MuiPagination-ul": {
										gap: 0.5,
										justifyContent: "flex-end",
									},
								}}
							/>
						</Box>
					)}
				</>
			)}
		</Box>
	);
};

export default PaginatedSearchWrapper;
